import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Layout } from "./components/Layout";
import {
  RequireAdmin,
  RequireAuth,
  RequireVerifiedEmail,
} from "./components/RouteGuards";
import { Spinner } from "./components/ui";
import { LANGUAGE_STORAGE_KEY, detectLanguage, localizedPath } from "./i18n";
import { LanguageProvider, useLanguage } from "./i18n/LanguageProvider";

// Public pages load eagerly: they are the first thing most visitors see and
// must not wait on an extra round trip.
import { HomePage } from "./pages/public/HomePage";
import { AboutPage } from "./pages/public/AboutPage";
import { ServicesPage } from "./pages/public/ServicesPage";
import { CheckInPage } from "./pages/public/CheckInPage";
import { PartnershipsPage } from "./pages/public/PartnershipsPage";
import { CareersPage } from "./pages/public/CareersPage";
import { ContactPage } from "./pages/public/ContactPage";
import { PrivacyPage } from "./pages/public/PrivacyPage";
import { TermsPage } from "./pages/public/TermsPage";
import { NotFoundPage } from "./pages/public/NotFoundPage";

/**
 * Everything behind sign-in is code-split.
 *
 * The portal pulls in the Firebase auth and storage SDKs and the recorder;
 * none of that belongs in the bundle a first-time visitor downloads to read
 * the home page.
 */
const SignInPage = lazy(() => import("./pages/auth/SignInPage"));
const SignUpPage = lazy(() => import("./pages/auth/SignUpPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/auth/VerifyEmailPage"));

const AccountLayout = lazy(() => import("./pages/account/AccountLayout"));
const AccountOverviewPage = lazy(
  () => import("./pages/account/AccountOverviewPage"),
);
const ProfilePage = lazy(() => import("./pages/account/ProfilePage"));
const ApplicationPage = lazy(() => import("./pages/account/ApplicationPage"));
const VideosPage = lazy(() => import("./pages/account/VideosPage"));
const RecordPage = lazy(() => import("./pages/account/RecordPage"));

const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminSubmissionsPage = lazy(
  () => import("./pages/admin/AdminSubmissionsPage"),
);
const AdminSubmissionDetailPage = lazy(
  () => import("./pages/admin/AdminSubmissionDetailPage"),
);
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));
const AdminMessagesPage = lazy(() => import("./pages/admin/AdminMessagesPage"));
const AdminAuditPage = lazy(() => import("./pages/admin/AdminAuditPage"));
const AdminBootstrapPage = lazy(
  () => import("./pages/admin/AdminBootstrapPage"),
);

const PageFallback = () => (
  <div className="gw-container gw-section">
    <Spinner />
  </div>
);

/**
 * Sends a visitor with no language in the URL to one that has it.
 *
 * Every real page lives under an explicit language prefix, so this is the
 * only route without one. The redirect is a replace so the bare URL does
 * not clutter the back button.
 */
const LanguageRedirect = () => {
  const location = useLocation();

  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    stored = null;
  }

  const language = detectLanguage(
    stored,
    typeof navigator !== "undefined"
      ? (navigator.languages ?? [navigator.language])
      : [],
  );

  const target = `${localizedPath(language, location.pathname)}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
};

/**
 * The learning framework used to be its own page; it now lives on the About
 * page. Hosting answers the old URL with a 301, this covers in-app links.
 */
const LearningModelRedirect = () => {
  const { path } = useLanguage();
  return <Navigate to={`${path("/about")}#framework`} replace />;
};

const LanguageLayout = () => (
  <LanguageProvider>
    <Suspense fallback={<PageFallback />}>
      <LanguageScopedRoutes />
    </Suspense>
  </LanguageProvider>
);

export const AppRoutes = () => (
  <Routes>
    {/*
      Hosting is configured with trailingSlash: false, so the language root
      is `/zh-CN` rather than `/zh-CN/`. Both the bare prefix and nested
      paths must mount the same layout.
    */}
    <Route path="/:lang" element={<LanguageLayout />} />
    <Route path="/:lang/*" element={<LanguageLayout />} />
    <Route path="*" element={<LanguageRedirect />} />
  </Routes>
);

/**
 * Routes inside a language segment.
 *
 * Paths here are language-neutral; the prefix is handled by the parent
 * route, which is what lets a language switch keep the current page.
 */
const LanguageScopedRoutes = () => (
  <Routes>
    <Route element={<Layout />}>
      <Route index element={<HomePage />} />
      <Route path="about" element={<AboutPage />} />
      <Route path="services" element={<ServicesPage />} />
      <Route path="learning-model" element={<LearningModelRedirect />} />
      <Route path="check-in" element={<CheckInPage />} />
      <Route path="partnerships" element={<PartnershipsPage />} />
      <Route path="careers" element={<CareersPage />} />
      <Route path="contact" element={<ContactPage />} />
      <Route path="privacy" element={<PrivacyPage />} />
      <Route path="terms" element={<TermsPage />} />

      <Route path="sign-in" element={<SignInPage />} />
      <Route path="sign-up" element={<SignUpPage />} />
      <Route path="reset-password" element={<ResetPasswordPage />} />
      <Route path="verify-email" element={<VerifyEmailPage />} />

      <Route element={<RequireAuth />}>
        <Route path="account" element={<AccountLayout />}>
          <Route index element={<AccountOverviewPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="application" element={<ApplicationPage />} />
          <Route
            path="application/:submissionId"
            element={<ApplicationPage />}
          />
          <Route path="videos" element={<VideosPage />} />
        </Route>
      </Route>

      {/* The recorder is its own full-width page rather than a panel inside
          the account layout: it needs the whole viewport on a phone, and it
          owns camera hardware that should be released on navigation. */}
      <Route element={<RequireVerifiedEmail />}>
        <Route path="video-introduction" element={<RecordPage />} />
      </Route>

      <Route element={<RequireAdmin />}>
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<AdminSubmissionsPage />} />
          <Route
            path="submissions/:submissionId"
            element={<AdminSubmissionDetailPage />}
          />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="messages" element={<AdminMessagesPage />} />
          <Route path="audit" element={<AdminAuditPage />} />
        </Route>
      </Route>

      {/* Deliberately outside RequireAdmin: this is the route used when no
          administrator exists yet, so there is nobody to authorise it. It
          is protected by the server-side bootstrap secret instead. */}
      <Route path="admin-setup" element={<AdminBootstrapPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes>
);
